/**
 * Created by Abner Sui on 04/18/2019.
 * Description:
 *
 * ------ maintenance history ------
 * Updated by Daniel on 10/18/2021, Added two options 'Find first note by Submitted Date' and 'Find first note by Display Date'
 */

import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { MultiSelDropdownFilterConfig } from '../../../../widgets/multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
import { Store, select } from '@ngrx/store';
import { fullEntryTypeSelector } from '../../../../redux/reducers/entry-type.reducer';
import { take, map, filter } from 'rxjs/operators';
import { EntryType } from '../../../../tamalelibs/models/entry-type.model';
import { Subscription } from 'rxjs';
import { TileColumnGroupSetting } from '../../../../tamalelibs/models/tile-column.model';
import { FilterPanelConfig, FilterPanelEvents, FilterPanelActions } from '../../../../widgets/filter-item/filter-panel/filter-panel.view-model';
import { FilterMetadataType } from '../../../../tamalelibs/components/filter-config';
import { TileFilterService } from '../../../../tamalelibs/services/tile-filter.service';
import { EntityService } from '../../../../tamalelibs/services/entity.service';
import * as _lodash from 'lodash';
import { FieldConfig, FieldConfigData, FieldEventType } from '../../../../widgets/field-selector/field-selector.model';
import { EntitiesFilter, StandardFilterItems, StartDateFilter } from '../tile-filter/tile-filter.model';
import { TileFilter } from '../../../../tamalelibs/models/tile.model';
import { CURRENT_USER } from '../../../../tamalelibs/constants/business.constants';
import { BriefConfig } from '../../../../widgets/widgets.model';
import { TileColumnSettingEvents } from '../tile-setting.model';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'tile-column-group',
    templateUrl: './tile-column-group.component.html',
    styleUrls: ['./tile-column-group.component.scss']
})
export class TileColumnGroupComponent implements OnInit, OnDestroy {

    @Input() config: TileColumnGroupSetting;
    @Input()
    briefConfig: BriefConfig;

    filterConfig: FilterPanelConfig = new FilterPanelConfig();
    fieldConfig: FieldConfig;
    dropdownConfig: MultiSelDropdownFilterConfig = new MultiSelDropdownFilterConfig();

    private _entryTypeList: Array<EntryType>;
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _store: Store<any>,
        private _tileFilterService: TileFilterService,
        private _entityService: EntityService
    ) { }

    ngOnInit(): void {
        this.filterConfig.isShowDynamicDate = true;
        this.initFieldSelector();
        this.initFilter();
        this.fieldConfigSubject();
        this.filterConfigSubject();
        const checkedIds: string = this.config.filters && this.config.filters.filters[0] && this.config.filters.filters[0].value || '';
        this._store.pipe(select(fullEntryTypeSelector),
            take(1)
        ).subscribe(res => {
            this._entryTypeList = res;
            res.forEach(one => {
                this.dropdownConfig.data.push({
                    id: one.id,
                    name: one.name,
                    checked: checkedIds.includes(one.id),
                });
            });
        });
        this._destroySubscriptions.push(
            this.dropdownConfig.onChangeValue$.subscribe(data => {
                const valueList: Array<any> = typeof data === 'string' ? this._entryTypeList : data;
                const value: string = '(' + valueList.map(item => '\'' + item.id + '\'').join(',') + ')';
                const originalText: string = valueList.map(item => item.name).join(', ');
                // the default entry type filter
                const _filter = {
                    value: value,
                    field: 'entry__type_id',
                    operator: 'in',
                    originalText: originalText,
                };
                // update the values of entry type if it exists in the filter config
                if (this.config.filters && this.config.filters.filters && this.config.filters.filters.length > 0) {
                    const entryTypeFilter = this.config.filters.filters.filter(item => item.field === 'entry__type_id');
                    if (entryTypeFilter.length > 0) { // update value and originalText
                        entryTypeFilter[0].value = value;
                        entryTypeFilter[0].originalText = originalText;
                    } else { // convert the original values
                        this.config.filters.filters[0] = _filter;
                    }
                } else {
                    // create a new entry type filter
                    this.config.filters = new TileFilter();
                    this.config.filters.filters[0] = _filter;
                }
                this.briefConfig.feedbackSubject$.next({
                    type: TileColumnSettingEvents.COLUMN_GROUP_NOTE_TYPE_CHANGE,
                });
            }),
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    getNewEntityInfo() {
        const entityIds = [];
        const needUpdate = (item) => {
            return item.metadataType === FilterMetadataType.Entity || item.metadataType === FilterMetadataType.Source ||
                item.metadataType === FilterMetadataType.Submitter || item.metadataType === FilterMetadataType.Team ||
                item.metadataType === FilterMetadataType.EntryEntity;
        };
        this.filterConfig.filtersConfigList.forEach(item => {
            if (needUpdate(item) && item.value) {
                item.value.forEach(val => {
                    if (val.id !== CURRENT_USER.id) {
                        entityIds.push(val.id);
                    }
                });
                const index = item.value.findIndex(data => data.id === CURRENT_USER.id);
                if (index !== -1) {
                    item.value[index] = CURRENT_USER;
                }
            }
        });
        this.reloadGroupFilters();
        if (entityIds.length !== 0) {
            this._entityService.getEntityListByIdsQuick(entityIds).pipe(
                map(response => this._entityService.mapEntityBriefList(response)),
                map(entities => {
                    const entityMap = new Map();
                    entities.forEach(element => entityMap.set(element.id, element));
                    return entityMap;
                })
            ).subscribe((entityMap => {
                this.filterConfig.filtersConfigList.forEach(item => {
                    if (needUpdate(item) && item.value) {
                        item.value = item.value.map(element => {
                            if (entityMap.has(element.id)) {
                                return entityMap.get(element.id);
                            }
                            return element;
                        });
                        item.value = item.value.filter(value => !!value);
                    }
                });
                this.reloadGroupFilters();
            }));
        }
    }

    onChangeOptions(value) {
        this.briefConfig.feedbackSubject$.next({
            type: TileColumnSettingEvents.COLUMN_GROUP_OPTIONS_CHANGE,
        });
    }

    reloadGroupFilters() {
        this.filterConfig.action$.next({ type: FilterPanelActions.reloadFilter, payload: this.filterConfig.filtersConfigList });
    }

    initFilter() {
        if (this.config.filters && this.config.filters.filters && this.config.filters.filters[1] &&
            this.config.filters.filters[1].filters && this.config.filters.filters[1].filters.length > 0) {
            this.config.filters.filters[1].filters.forEach(item => {
                const filterConfig = this._tileFilterService.convertFilterTileToCommon(item);
                if (filterConfig) {
                    this.filterConfig.filtersConfigList.push(filterConfig);
                }
            });
        }
        this.getNewEntityInfo();
    }

    initFieldSelector() {
        this.fieldConfig = new FieldConfig();
        this.fieldConfig.config = new FieldConfigData();
        this.fieldConfig.config.standardItem = StandardFilterItems.concat(EntitiesFilter); // add entity filter to the container
        this.fieldConfig.config.showAdhoc = true;
        this.fieldConfig.config.otherAdhoc = [StartDateFilter];
        this.fieldConfig.config.showSearch = true;
    }

    filterConfigSubject() {
        this._destroySubscriptions.push(
            this.filterConfig.events$.pipe(
                filter((data) => data.type === FilterPanelEvents.change || data.type === FilterPanelEvents.delete)
            ).subscribe((data) => {
                this.filterConfig.filtersConfigList = _lodash.cloneDeep(data.payload);
                this._formatFilterData();
            })
        );
    }

    fieldConfigSubject() {
        this._destroySubscriptions.push(
            this.fieldConfig.events$.pipe(
                filter(data => data.type === FieldEventType.select)
            ).subscribe((data) => {
                let filtersConfig;
                if (data.payload.type === 'standard') {
                    filtersConfig = this._tileFilterService.getNewFilterDefaultInstance(data.payload.text, data.payload.value);
                } else {
                    filtersConfig = this._tileFilterService.getAdhocFilterDefaultInstance(data.payload);
                }
                this.filterConfig.filtersConfigList.push(filtersConfig);
                this.filterConfig.action$.next({ type: FilterPanelActions.addFilter, payload: [filtersConfig] });
                this._formatFilterData();
            })
        );
    }

    private _formatFilterData() {
        let filters;
        if (this.filterConfig.filtersConfigList.length > 0) {
            filters = new TileFilter();
            if (this.config.filters) {
                this.config.filters.filters[1] = {
                    logicalOperator: 'and',
                    filters: []
                };
            } else {
                this.config.filters = filters;
                this.config.filters.filters[1] = filters;
            }
            filters = this._tileFilterService.getFilters(this.filterConfig.filtersConfigList);
            this.config.filters.filters[1].filters = filters;
        } else {
            this.config.filters.filters.splice(1, 1);
        }

        this.briefConfig.feedbackSubject$.next({
            type: TileColumnSettingEvents.COLUMN_GROUP_FILTER_CHANGE,
        });
    }
}
